import { css } from "glamor";
import React from "react";
import { FiDownload } from "react-icons/fi";
import { FaCalculator } from "react-icons/fa";
import { IoDocumentText } from "react-icons/io5";
import { RiPushpinFill } from "react-icons/ri";
import { FaQuestionCircle } from "react-icons/fa";
import { Menu, Tab } from "semantic-ui-react";
import LessonFilesContainer from "../lessonFiles/LessonFilesContainer";
import LessonQuestionContainer from "./LessonQuestionContainer";
import Button from "../library/Button";
import Spinner from "../library/Spinner";
import { colors, spacing, styles, uiColors } from "../library/theme";
import Annotations from "../annotations/Annotations";
import LessonTypeformSurveyContainer from "./LessonTypeformSurveyContainer";

const calculatorWrapper = css({
  margin: `${spacing.m} auto`,
  height: "380px",
  width: "600px",
  overflow: "hidden",
});
const iframeClass = css({
  position: "relative",
  top: -220,
  left: -100,
});
const buttonLoader = {
  marginRight: spacing.s,
};
const examCTAWrapper = css({
  borderBottom: `1px solid ${uiColors.border}`,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  paddingBottom: spacing.m,
});
const htmlClass = css({
  ...styles.wrappingText,
  whiteSpace: "pre-line",
  paddingTop: spacing.m,
  "& + &": {
    marginTop: spacing.m,
  },
});
const tabWrapper = css({
  "& .ui.tabular.menu": {
    overflowX: "auto",
    scrollbarWidth: "none",
    "::-webkit-scrollbar": {
      display: "none",
    },
  },
  "& .ui.tabular.menu .item": {
    fontWeight: "600",
    color: colors.greyShadow,
    border: "none",
    borderBottom: "4px solid transparent",
    "> svg": {
      color: colors.primaryShadow,
      marginRight: spacing.xs,
    },
  },
  "& .ui.tabular.menu .active.item": {
    background: "transparent",
    margin: 0,
    border: "none",
    padding: spacing.m,
    borderBottom: `4px solid ${colors.primaryLighter}`,
  },
  "& .ui.tab.active, .ui.tab.open": {
    marginTop: spacing.m,
    padding: 0,
    background: "transparent !important",
    border: "none",
    boxShadow: "none",
  },
});
const descriptionStyle = {
  width: "100%",
  overflowX: "auto",
};

const LessonActions = React.forwardRef(function LessonActions(props, ref) {
  const {
    calculatorEnabled,
    lesson,
    course,
    createMockHandler,
    loadingMock,
    annotations,
    canAccessCourse,
    hasFiles,
    hasQuestions,
    tabControlRef,
  } = props;

  const questionsTabIndexRef = React.useRef(-1);

  const panes = React.useMemo(() => {
    let tabPanes = [];
    questionsTabIndexRef.current = -1;
    if (
      lesson?.typeformUrl ||
      lesson?.description ||
      (course && lesson?.mockTemplateId)
    ) {
      tabPanes.push({
        menuItem: (
          <Menu.Item key="content">
            <IoDocumentText size={22} />
            Descrição
          </Menu.Item>
        ),
        render: () => (
          <Tab.Pane attached={false}>
            <div style={descriptionStyle}>
              {!!course && !!lesson?.mockTemplateId && (
                <div className={examCTAWrapper}>
                  <Button
                    color="green"
                    onClick={createMockHandler}
                    disabled={loadingMock}
                  >
                    {loadingMock && (
                      <Spinner
                        color="black"
                        inline
                        size="tiny"
                        style={buttonLoader}
                      />
                    )}
                    Ir para Prova {course?.name}
                  </Button>
                </div>
              )}

              {lesson.description && (
                <div
                  className={htmlClass}
                  dangerouslySetInnerHTML={{
                    __html: lesson.description,
                  }}
                ></div>
              )}

              <LessonTypeformSurveyContainer lessonId={lesson.id} />
            </div>
          </Tab.Pane>
        ),
      });
    }

    if (hasFiles) {
      tabPanes.push({
        menuItem: (
          <Menu.Item key="downloads">
            <FiDownload size={22} /> Downloads
          </Menu.Item>
        ),
        render: () => (
          <Tab.Pane attached={false}>
            <div>
              <LessonFilesContainer lessonId={lesson?.id} />
            </div>
          </Tab.Pane>
        ),
      });
    }

    if (canAccessCourse) {
      tabPanes.push({
        menuItem: (
          <Menu.Item key="annotations">
            <RiPushpinFill size={22} /> Anotações
          </Menu.Item>
        ),
        render: () => (
          <Tab.Pane attached={false}>
            <Annotations lesson={lesson} />
          </Tab.Pane>
        ),
      });
    }

    if (hasQuestions) {
      questionsTabIndexRef.current = tabPanes.length;
      tabPanes.push({
        menuItem: (
          <Menu.Item key="questions">
            <FaQuestionCircle size={22} /> Questões
          </Menu.Item>
        ),
        render: () => (
          <Tab.Pane attached={false}>
            <LessonQuestionContainer />
          </Tab.Pane>
        ),
      });
    }

    if (calculatorEnabled) {
      tabPanes.push({
        menuItem: (
          <Menu.Item key="calculator">
            <FaCalculator size={22} /> Calculadora
          </Menu.Item>
        ),
        render: () => (
          <Tab.Pane attached={false}>
            <div className={calculatorWrapper}>
              <iframe
                className={iframeClass}
                src="https://www.accountcontabilidade.com.br/hp12c/"
                frameBorder="0"
                border="0"
                width="800px"
                height="590px"
                scrolling="no"
              ></iframe>
            </div>
          </Tab.Pane>
        ),
      });
    }

    return tabPanes;
  }, [
    lesson,
    annotations,
    canAccessCourse,
    hasFiles,
    hasQuestions,
    calculatorEnabled,
    course,
    createMockHandler,
    loadingMock,
  ]);

  const [activeIndex, setActiveIndex] = React.useState(0);
  const handleTabChange = React.useCallback((e, { activeIndex }) => {
    setActiveIndex(activeIndex);
  }, []);

  React.useEffect(() => {
    if (lesson?.id) setActiveIndex(0);
  }, [lesson?.id]);

  // Expõe função para mudar a tab de fora do componente
  // Usado pelo LessonQuestionAlert para abrir a tab de questões
  React.useImperativeHandle(tabControlRef || ref, () => ({
    setActiveTabIndex: (index) => {
      if (index >= 0 && index < panes.length) {
        setActiveIndex(index);
        // Simula o evento de mudança de tab para garantir que o Semantic UI Tab seja atualizado
        // Usa setTimeout para garantir que o estado seja atualizado primeiro
        setTimeout(() => {
          handleTabChange(null, { activeIndex: index });
        }, 0);
      }
    },
    getActiveTabIndex: () => activeIndex,
    getQuestionsTabIndex: () => {
      // Retorna o índice armazenado da tab de questões
      return questionsTabIndexRef.current;
    },
  }), [activeIndex, panes, tabControlRef, ref, handleTabChange]);

  return (
    <div className={tabWrapper}>
      <Tab
        panes={panes}
        activeIndex={activeIndex}
        onTabChange={handleTabChange}
      />
    </div>
  );
});

export default LessonActions;
